import { useState, useEffect, useRef } from "react";
import DataTable from "../../Common/DataTable/DataTable";
import { getAllCoupons, getCouponsDetails } from "../../api";
import CustomSpinner from "../../Common/Spinner";
import { format } from "date-fns";
import styles from "./Coupon.module.css";
import { Offcanvas, Card, Accordion } from "react-bootstrap";
import SearchFilterBar from "../../Common/SearchFilter/SearchFilterBar";
import { Filter } from "../../Common";
import Result from "../../Common/V2/Widgets/Result";
import NoDataFound from "../../Common/V2/images/noDataFound";
import { InvCouponsColumns } from "../../utils/tableHeader";
import CouponDetails from "./CouponDetails";
import CreateCoupon from "./CreateCoupon";
import withFeatureActions from "../../withFeatureActions";
import { Button } from "../../Common";
import singleBackArrow from "../../public/images/singleBackArrow.svg";

const Coupons = ({ featureAccess }) => {
  const { writeAccess } = featureAccess;
  const timeoutRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [couponData, setCouponData] = useState([]);
  const [showCanvas, setShowCanvas] = useState(false);
  const [canvasData, setCanvasData] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [showFilter, setShowFilter] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [status, setStatus] = useState("");
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [searchLoading, setSearchLoading] = useState(false);
  const [stepToShow, setStepToShow] = useState(1);
  const [mode, setMode] = useState("create");
  const [formData, setFormData] = useState(null);
  const isFirstRender = useRef(true);

  const fetchCoupons = async (
    page = 0,
    search = searchText,
    statusParam = status,
    start = startDate,
    end = endDate
  ) => {
    try {
      setLoading(true);
      setSearchLoading(true);
      const response = await getAllCoupons(
        search,
        statusParam,
        start ? format(start, "yyyy-MM-dd") : "",
        end ? format(end, "yyyy-MM-dd") : "",
        page,
        pageSize
      );

      if (response?.data?.code === 200) {
        const list = response?.data?.data?.list || [];
        const count = response?.data?.data?.total || 0;

        setCouponData(list);
        setTotalPages(Math.ceil(count / pageSize));
      } else {
        setCouponData([]);
      }
    } catch (error) {
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const handleFilterClose = () => {
    setShowFilter(false);

    setStartDate("");
    setEndDate("");
    setStatus("");
    setSearchText("");

    fetchCoupons(0, "", "", "", "");
  };

  const handleViewDetails = async (couponCode) => {
    setLoading(true);
    try {
      const res = await getCouponsDetails(couponCode);
      if (res?.status === 200) {
        setCanvasData(res?.data?.data);
        setShowCanvas(true);
      } else {
        setCanvasData(null);
      }
    } catch (err) {
      setCanvasData(null);
    }
    setLoading(false);
  };

  const handleClickRow = async (data) => {
    if (data.code) {
      setCanvasData({});
      setShowCanvas(true);
      await handleViewDetails(data.code);
      setStepToShow(1);
    }
  };

  const handleShowCreateCoupon = () => {
    setMode("create");
    setFormData(null);
    setStepToShow(2);
  };

  const handleClickUpdateCoupon = async (data) => {
    if (data && data.code) {
      setMode("update");
      await handleUpdateCoupon(data.code);
      setStepToShow(2);
    }
  };

  const handleUpdateCoupon = async (couponCode) => {
    setLoading(true);
    try {
      const res = await getCouponsDetails(couponCode);
      if (res?.status === 200) {
        const fetchedData = res?.data?.data;
        const transformedData = {
          code: fetchedData?.code || "",
          status: fetchedData?.status || "ACTIVE",
          expiryDt: fetchedData?.expiryDt ? new Date(fetchedData.expiryDt) : "",
          usageLimit: fetchedData?.usageLimit || "",
          title: fetchedData?.title || "",
          discountValue: fetchedData?.discountValue || "",
          discountType: fetchedData?.discountType || "",
          startDt: fetchedData?.startDt ? new Date(fetchedData.startDt) : "",
          // usageCount: fetchedData?.usageCount || 0,
          partnerCode: fetchedData?.partnerCode || "",
          planId: fetchedData?.planCode || "",
          comment: fetchedData?.comment || "",
        };
        setFormData(transformedData);
        setShowCanvas(false);
        setStepToShow(2);
      } else {
        setFormData(null);
      }
    } catch (err) {
      setFormData(null);
    }
    setLoading(false);
  };

  const handleBackToTable = () => {
    setCanvasData(null);
    setStepToShow(1);
    setShowCanvas(false);
    setFormData(null);
  };

  const handleCouponSuccess = () => {
    fetchCoupons(currentPage);
    handleBackToTable();
    setStepToShow(1);
  };

  const handleSearch = () => {
    setCurrentPage(0);
    fetchCoupons(0);
  };

  useEffect(() => {
    if (isFirstRender.current) return;

    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      handleSearch();
    }, 500);

    return () => clearTimeout(timeoutRef.current);
  }, [searchText]);

  useEffect(() => {
    fetchCoupons(currentPage);
  }, [currentPage]);

  useEffect(() => {
    isFirstRender.current = false;
    fetchCoupons(0);
  }, []);

  return (
    <div className={styles.container} style={{ position: "relative" }}>
      {stepToShow === 1 && (
        <>
          <SearchFilterBar
            inputText={searchText}
            handleInputChange={(e) => setSearchText(e.target.value)}
            setShowFilter={setShowFilter}
            showFilter={showFilter}
            setInputText={setSearchText}
            placeholder="Search by Coupon code"
            onFilterClose={handleFilterClose}
          />

          {showFilter && (
            <Card className={styles.customCard}>
              <Card.Body>
                <Filter
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  couponStatus={status}
                  setCouponStatus={setStatus}
                  showCouponStatus={true}
                  showPartner={false}
                  onSearch={() => fetchCoupons(0)}
                  loading={searchLoading}
                />
              </Card.Body>
            </Card>
          )}

          <div className={styles.dataTableContainer}>
            {loading ? (
              <CustomSpinner />
            ) : couponData?.length > 0 ? (
              <DataTable
                columns={InvCouponsColumns(featureAccess)}
                list={couponData}
                rowClick={false}
                handleClickRow={handleClickRow}
                handleViewDetails={handleViewDetails}
                totalPages={totalPages}
                currentPage={currentPage + 1}
                setCurrentPage={setCurrentPage}
                pagination={true}
                title="Coupons"
                titleButton={
                  writeAccess && (
                    <Button
                      onClick={handleShowCreateCoupon}
                      title={"Create Coupon"}
                    />
                  )
                }
                handleUpdateCoupon={handleClickUpdateCoupon}
              />
            ) : (
              <div className={styles.resultContainer}>
                <Result
                  icon={<NoDataFound width={80} height={80} />}
                  title="No Data Found"
                  subtitle="Your Data will be displayed here"
                />
              </div>
            )}
          </div>

          <Offcanvas
            show={showCanvas}
            onHide={() => setShowCanvas(false)}
            placement="end"
            className={styles.offcanvas}
          >
            <Offcanvas.Header closeButton>
              <Offcanvas.Title>Coupon Details</Offcanvas.Title>
            </Offcanvas.Header>

            <Offcanvas.Body>
              {loading ? (
                <CustomSpinner />
              ) : canvasData ? (
                <Accordion defaultActiveKey="0">
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>Details</Accordion.Header>
                    <Accordion.Body>
                      <CouponDetails canvasData={canvasData} />
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              ) : (
                <p style={{ textAlign: "center" }}>No Data Available</p>
              )}
            </Offcanvas.Body>
          </Offcanvas>
        </>
      )}

      {stepToShow === 2 && (
        <div className={styles.createCouponContainer}>
          <div className={styles.backButtons}>
            <img
              alt="Back"
              src={singleBackArrow}
              onClick={handleBackToTable}
              className={styles.arrowButton}
            />
            <span
              style={{
                fontSize: "13px",
                marginLeft: "5px",
                cursor: "pointer",
                color: "white",
              }}
              onClick={handleBackToTable}
            >
              Back
            </span>
          </div>
          <CreateCoupon
            mode={mode}
            initialData={formData}
            onSuccess={handleCouponSuccess}
          />
        </div>
      )}
    </div>
  );
};

export default withFeatureActions(Coupons, "INVESTING", "Coupons");
